import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '../../context/AuthContext';
import { ProfileCompletionModal } from './ProfileCompletionModal';

/**
 * Phase 7-dashboard hotfix round 3 — progressive-disclosure profile gate.
 *
 * Earlier iterations auto-opened a soft modal once per browser session right after
 * signup. The new contract: the modal NEVER auto-opens. The candidate signs in,
 * lands on the home page, and is nudged toward profile completion only by:
 *   - the `<ProfileCompletionBanner>` on relevant pages (jobs, dashboard, etc.), and
 *   - `useProfileGate().openHard(...)` invoked from the Apply CTA on a job.
 *
 * State model:
 *   - `softOpen` — only opens when the candidate clicks the banner's "Complete now"
 *     button. Dismissible (X / backdrop / ESC) so they can keep browsing. Buttons:
 *     [Sign Out] [Save Profile].
 *   - `hardOpen` — opened imperatively from the Apply flow. Also dismissible —
 *     enforcement happens at the Apply level: closing the modal cancels the apply,
 *     a toast tells the candidate why.
 *
 * Mounted in `App.tsx` immediately after `<MasterDataProvider>` so it has Auth +
 * MasterData in scope when the modal opens.
 */

export interface ApplyContext {
  jobTitle: string;
  /** Fired after the modal saves a complete profile. */
  onAfterComplete?: () => void;
  /** Phase 7-dashboard hotfix round 3 — fires when the candidate dismisses the modal
   *  without saving. The Apply caller surfaces a toast ("Complete your profile to
   *  apply for jobs") so the cancellation reads as informative, not a silent failure. */
  onCancel?: () => void;
}

interface ProfileGateApi {
  openSoft: () => void;
  /** Opens the hard variant. **Always returns `true` today** — does NOT check
   *  `candidate.profile_completed` itself; the caller MUST do that against a
   *  fresh source (the existing callers re-fetch `/me` first; see
   *  `JobDetailPage.handleApply` / `MySavedJobsPage.handleApply`). The render
   *  gate `open={hardOpen && incomplete}` below still hides the modal when the
   *  cached `incomplete` flag is false, but a stale cache can leave a caller
   *  thinking "modal opened" when it didn't — which is why the truth check
   *  belongs at the call site against a fresh `/me`. The return value is
   *  preserved as `true` for backward compatibility with the original contract;
   *  do not rely on it for "did the modal actually render" decisions. */
  openHard: (ctx: ApplyContext) => boolean;
}

const ProfileGateContext = createContext<ProfileGateApi | null>(null);

export function useProfileGate(): ProfileGateApi {
  const ctx = useContext(ProfileGateContext);
  if (!ctx) throw new Error('useProfileGate must be used within <ProfileCompletionGate>');
  return ctx;
}

export function ProfileCompletionGate({ children }: { children: ReactNode }) {
  const { candidate, refresh } = useAuth();
  const [softOpen, setSoftOpen] = useState(false);
  const [hardOpen, setHardOpen] = useState(false);
  const [applyContext, setApplyContext] = useState<ApplyContext | null>(null);

  const incomplete = !!candidate && !candidate.profile_completed;

  const openSoft = useCallback(() => {
    setSoftOpen(true);
  }, []);

  const openHard = useCallback((ctx: ApplyContext) => {
    setApplyContext(ctx);
    setHardOpen(true);
    return true;
  }, []);

  const handleClose = useCallback(
    (variant: 'soft' | 'hard') => {
      if (variant === 'soft') {
        setSoftOpen(false);
      } else {
        // Hard variant: dismissing cancels the pending apply. Caller decides
        // whether to surface a toast — we just signal intent.
        const cancel = applyContext?.onCancel;
        setHardOpen(false);
        setApplyContext(null);
        if (cancel) setTimeout(() => cancel(), 0);
      }
    },
    [applyContext],
  );

  const handleSaved = useCallback(
    async (variant: 'soft' | 'hard') => {
      // Refresh the auth bootstrap so `profile_completed` flips and downstream UI
      // (banner, dashboard) updates without a full reload.
      await refresh();
      if (variant === 'soft') {
        setSoftOpen(false);
      } else {
        const pending = applyContext?.onAfterComplete;
        setHardOpen(false);
        setApplyContext(null);
        if (pending) {
          // Microtask gap so React flushes the close animation before the apply
          // network call kicks off.
          setTimeout(() => pending(), 0);
        }
      }
    },
    [applyContext, refresh],
  );

  const api = useMemo<ProfileGateApi>(() => ({ openSoft, openHard }), [openSoft, openHard]);

  return (
    <ProfileGateContext.Provider value={api}>
      {children}
      {/* Both modals are rendered side-by-side. Only one is ever open at a time;
          their internal state resets on close, so React unmount/remount discipline
          isn't required. */}
      {candidate && (
        <>
          <ProfileCompletionModal
            open={softOpen && incomplete}
            variant="soft"
            onClose={() => handleClose('soft')}
            onSaved={() => handleSaved('soft')}
          />
          <ProfileCompletionModal
            open={hardOpen && incomplete}
            variant="hard"
            applyContext={applyContext}
            onClose={() => handleClose('hard')}
            onSaved={() => handleSaved('hard')}
          />
        </>
      )}
    </ProfileGateContext.Provider>
  );
}
